<script setup>
import {computed} from "vue";
import SourceGA from "@/components/reporting/partials/SourceGA.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import _ from "lodash";
import moment from "moment";
import ReportingEmpty from "@/components/reporting/partials/ReportingEmpty.vue";
import useTrafficStore from "@/composables/useTrafficStore";
import useAccountMissing from "@/composables/useAccountMissing";
import Reporting from "@/services/reporting";
import {useQueries} from "@tanstack/vue-query";
import useQueriesState from "@/composables/useQueriesState";
import ErrorBlock from "@/components/reporting/partials/ErrorBlock.vue";
import ConnectAccount from "@/components/reporting/partials/ConnectAccount.vue";
import BarChart from "@/components/global-themed/charts/BarChart.vue";

const colors = ['#99D4FF', '#AA00FF', '#00CCCC', '#FF4D88',];
const compareColors = ['#0094FF', '#AA00FF'];

const {fromDate, toDate, compareFromDate, compareToDate, range: dateRange, compareRange: compareDateRange} = useTrafficStore();

const {isAccountMissing, setIsAccountMissing, checkResponseForAccountMissing} = useAccountMissing();

const fetchDevices = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return null;
  setIsAccountMissing(false);
  try {
    return (await Reporting.fetchDevices(fromDate, toDate)).data.data;
  } catch (e) {
    checkResponseForAccountMissing(e.response);
    throw e;
  }
}

const results = useQueries({
  queries: [
    {
      queryKey: ['reporting-devices', 'reporting', fromDate, toDate],
      queryFn: async () => fetchDevices(fromDate.value, toDate.value),
    },
    {
      queryKey: ['reporting-devices', 'reporting', compareFromDate, compareToDate],
      queryFn: async () => fetchDevices(compareFromDate.value, compareToDate.value),
    },
  ],
});

const {data, comparedData, isLoading: loading, isError} = useQueriesState(results);

const getDeviceLabel = (device) => {
  if (device === 'smart tv') return 'Smart TV';

  return _.startCase(device);
}

const getDateRangeLabel = (dateRange) => {
  if (!dateRange || !dateRange[0] || !dateRange[1]) return '';

  return `${formatDateLabel(dateRange[0])} - ${formatDateLabel(dateRange[1])}`
}

const formatDateLabel = (date) => moment(date).format('DD/MM/YYYY');

const series = computed(() => {
  const series = [
    {
      name: getDateRangeLabel(dateRange.value),
      data: (data.value ?? []).map(col => ({
        x: getDeviceLabel(col.device),
        y: col.value,
      })),
    },
  ];
  if (!comparedData.value) return series

  return [
    ...series,
    {
      name: getDateRangeLabel(compareDateRange.value),
      data: (comparedData.value ?? []).map(col => ({
        x: getDeviceLabel(col.device),
        y: col.value,
      })),
    },
  ];
});

const options = computed(() => (
  {
    plotOptions: {
      bar: {
        distributed: !comparedData.value,
        horizontal: true,
        borderRadius: 6,
        borderRadiusApplication: 'end',
        barHeight: !comparedData.value ? '60%' : '40%',
      }
    },
    colors: !comparedData.value ? colors : compareColors,
    legend: {
      show: !comparedData.value,
    },
    dataLabels: {
      enabled: false,
    },
  }
))

</script>

<template>
  <div>
    <h3 class="tw-text-xl tw-text-font-primary tw-font-bold tw-font-sans tw-mb-4">
      Top Devices
    </h3>
    <div
      v-if="!data?.length"
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-w-full tw-mb-8"
    >
      <LoadingBlock v-if="loading" />
      <ConnectAccount v-else-if="isAccountMissing" />
      <ErrorBlock v-else-if="isError" />
      <ReportingEmpty v-else />

      <SourceGA />
    </div>
    <div
      v-else
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-mt-6 tw-mb-8"
    >
      <BarChart
        :series="series"
        :options="options"
        class="top-devices-chart apexcharts-bar-chart"
      />

      <SourceGA />
    </div>
  </div>
</template>